import { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Plus, Clock, User, Car, Phone, Mail, Trash2, Edit2, X, MessageCircle, Send, Search, Loader2, FileText, ArrowRight, Bell, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Appointments({ user }) {
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDialog, setShowDialog] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [reminderSettings, setReminderSettings] = useState({ reminder_message_template: "" });
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderPhones, setReminderPhones] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    customer_fiscal_code: "",
    vehicle_plate: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_version: "",
    vehicle_year: "",
    vehicle_vin: "",
    vehicle_fuel_type: "",
    appointment_date: "",
    appointment_time: "",
    service_type: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
    fetchReminderSettings();
  }, []);

  const fetchData = async () => {
    try {
      const [appointmentsRes, customersRes] = await Promise.all([
        authFetch(`${API}/appointments`, { }),
        authFetch(`${API}/customers`, { })
      ]);
      if (appointmentsRes.ok) {
        const data = await appointmentsRes.json();
        setAppointments(data);
      }
      if (customersRes.ok) {
        setCustomers(await customersRes.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento appuntamenti");
    } finally {
      setLoading(false);
    }
  };

  const fetchReminderSettings = async () => {
    try {
      const res = await authFetch(`${API}/officina/settings`, { });
      if (res.ok) {
        const data = await res.json();
        setReminderSettings({ reminder_message_template: data.reminder_message_template || "" });
      }
    } catch (error) {
      console.error("Error fetching reminder settings:", error);
    }
  };

  // Vehicle lookup from tuttotarghe.it API
  const handleVehicleLookup = async () => {
    const plate = formData.vehicle_plate?.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    
    if (!plate || plate.length < 5) {
      toast.error("Inserisci una targa valida (minimo 5 caratteri)");
      return;
    }

    setLookupLoading(true);
    try {
      const response = await authFetch(`${API}/vehicles/lookup/${plate}`, { });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          vehicle_plate: data.licensePlate || plate,
          vehicle_brand: data.brand || "",
          vehicle_model: data.model || "",
          vehicle_version: data.modelDetail || "",
          vehicle_fuel_type: data.fuelType || "",
          vehicle_year: data.buildYear?.toString() || ""
        }));
        toast.success(`Dati veicolo caricati (${data.source === 'cache' ? 'archivio locale' : 'TuttoTarghe'})`);
      } else if (response.status === 404) {
        toast.info("Veicolo non trovato - inserisci i dati manualmente");
      } else {
        toast.error("Errore nella ricerca");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setLookupLoading(false);
    }
  };

  // Customer selection
  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerId(customerId);
    if (customerId) {
      const customer = customers.find(c => c.customer_id === customerId);
      if (customer) {
        setFormData(prev => ({
          ...prev,
          customer_id: customerId,
          customer_name: customer.name || "",
          customer_email: customer.email || "",
          customer_phone: customer.phone || "",
          customer_address: customer.address || "",
          customer_fiscal_code: customer.fiscal_code || ""
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        customer_id: "",
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_address: "",
        customer_fiscal_code: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingAppointment 
        ? `${API}/appointments/${editingAppointment.appointment_id}`
        : `${API}/appointments`;
      
      const response = await authFetch(url, {
        method: editingAppointment ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingAppointment ? "Appuntamento aggiornato" : "Appuntamento creato");
        setShowDialog(false);
        setEditingAppointment(null);
        resetForm();
        fetchData();
      } else {
        throw new Error("Errore");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo appuntamento?")) return;
    
    try {
      const response = await authFetch(`${API}/appointments/${appointmentId}`, {
        method: "DELETE"
      });

      if (response.ok) {
        toast.success("Appuntamento eliminato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleStatusChange = async (appointmentId, status) => {
    try {
      const response = await authFetch(`${API}/appointments/${appointmentId}/status?status=${status}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      customer_address: "",
      customer_fiscal_code: "",
      vehicle_plate: "",
      vehicle_brand: "",
      vehicle_model: "",
      vehicle_version: "",
      vehicle_year: "",
      vehicle_vin: "",
      vehicle_fuel_type: "",
      appointment_date: "",
      appointment_time: "",
      service_type: "",
      notes: ""
    });
    setSelectedCustomerId("");
  };

  const openEditDialog = (apt) => {
    setEditingAppointment(apt);
    setSelectedCustomerId(apt.customer_id || "");
    setFormData({
      customer_id: apt.customer_id || "",
      customer_name: apt.customer_name,
      customer_email: apt.customer_email,
      customer_phone: apt.customer_phone,
      customer_address: apt.customer_address || "",
      customer_fiscal_code: apt.customer_fiscal_code || "",
      vehicle_plate: apt.vehicle_plate,
      vehicle_brand: apt.vehicle_brand || "",
      vehicle_model: apt.vehicle_model,
      vehicle_version: apt.vehicle_version || "",
      vehicle_year: apt.vehicle_year || "",
      vehicle_vin: apt.vehicle_vin || "",
      vehicle_fuel_type: apt.vehicle_fuel_type || "",
      appointment_date: apt.appointment_date,
      appointment_time: apt.appointment_time,
      service_type: apt.service_type,
      notes: apt.notes || ""
    });
    setShowDialog(true);
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    return appointments.filter(apt => apt.appointment_date === dateStr);
  };

  const datesWithAppointments = appointments.map(apt => new Date(apt.appointment_date + "T00:00:00"));

  const serviceTypes = [
    "Tagliando",
    "Riparazione",
    "Diagnosi",
    "Revisione",
    "Cambio Gomme",
    "Carrozzeria",
    "Altro"
  ];

  const statusColors = {
    scheduled: "status-scheduled",
    in_progress: "status-in_progress",
    completed: "status-completed",
    cancelled: "status-cancelled"
  };

  const statusLabels = {
    scheduled: "Programmato",
    in_progress: "In Corso",
    completed: "Completato",
    cancelled: "Annullato"
  };

  // Genera messaggio appuntamento
  const generateAppointmentMessage = (apt) => {
    const dateFormatted = new Date(apt.appointment_date + "T00:00:00").toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
    
    return `Gentile ${apt.customer_name},

Le confermiamo il Suo appuntamento:

📅 Data: ${dateFormatted}
🕐 Ora: ${apt.appointment_time}
🚗 Veicolo: ${apt.vehicle_model} - ${apt.vehicle_plate}
🔧 Servizio: ${apt.service_type}
${apt.notes ? `\n📝 Note: ${apt.notes}` : ''}

La aspettiamo!

${user?.business_name || user?.name || 'La nostra officina'}`;
  };

  // Invia via WhatsApp
  const sendWhatsApp = (apt) => {
    const phone = apt.customer_phone.replace(/\D/g, '');
    const phoneWithCountry = phone.startsWith('39') ? phone : `39${phone}`;
    const message = encodeURIComponent(generateAppointmentMessage(apt));
    const url = `https://wa.me/${phoneWithCountry}?text=${message}`;
    window.open(url, '_blank');
    toast.success("WhatsApp aperto");
  };

  // Invia via Email
  const sendEmail = (apt) => {
    const subject = encodeURIComponent(`Conferma Appuntamento - ${apt.appointment_date} ore ${apt.appointment_time}`);
    const body = encodeURIComponent(generateAppointmentMessage(apt));
    const url = `mailto:${apt.customer_email}?subject=${subject}&body=${body}`;
    window.location.href = url;
    toast.success("Client email aperto");
  };

  // Get tomorrow's date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // Filter appointments for tomorrow
  const tomorrowAppointments = appointments.filter(apt => 
    apt.appointment_date === getTomorrowDate() && 
    apt.status !== "completed" && 
    apt.status !== "cancelled"
  );

  // Generate reminder message for an appointment
  const generateReminderMessage = (apt) => {
    const dateFormatted = new Date(apt.appointment_date + "T00:00:00").toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });

    // Use custom template if available, otherwise use default
    if (reminderSettings.reminder_message_template) {
      return reminderSettings.reminder_message_template
        .replace(/{nome}/g, apt.customer_name)
        .replace(/{data}/g, dateFormatted)
        .replace(/{ora}/g, apt.appointment_time)
        .replace(/{veicolo}/g, apt.vehicle_model || "")
        .replace(/{targa}/g, apt.vehicle_plate)
        .replace(/{servizio}/g, apt.service_type)
        .replace(/{note}/g, apt.notes || "")
        .replace(/{officina}/g, user?.business_name || user?.name || "La nostra officina");
    }

    // Default message
    return `Gentile ${apt.customer_name},

Le ricordiamo il Suo appuntamento per DOMANI:

📅 Data: ${dateFormatted}
🕐 Ora: ${apt.appointment_time}
🚗 Veicolo: ${apt.vehicle_model} - ${apt.vehicle_plate}
🔧 Servizio: ${apt.service_type}
${apt.notes ? `\n📝 Note: ${apt.notes}` : ''}

La aspettiamo!

${user?.business_name || user?.name || 'La nostra officina'}`;
  };

  // Copy reminder message to clipboard and show phone numbers
  const handleBulkReminder = () => {
    const pendingReminders = tomorrowAppointments.filter(apt => !apt.reminder_sent);
    
    if (pendingReminders.length === 0) {
      toast.info("Tutti i promemoria sono già stati inviati!");
      return;
    }

    // Get phone numbers with formatted info
    const phones = pendingReminders.map(apt => ({
      phone: apt.customer_phone.replace(/\D/g, ''),
      phoneWithCountry: apt.customer_phone.replace(/\D/g, '').startsWith('39') 
        ? apt.customer_phone.replace(/\D/g, '') 
        : `39${apt.customer_phone.replace(/\D/g, '')}`,
      name: apt.customer_name,
      time: apt.appointment_time,
      appointment_id: apt.appointment_id,
      message: generateReminderMessage(apt)
    }));

    setReminderPhones(phones);
    setShowReminderDialog(true);
  };

  // Copy single message to clipboard
  const copyMessageToClipboard = (message) => {
    navigator.clipboard.writeText(message);
    toast.success("Messaggio copiato negli appunti!");
  };

  // Mark single reminder as sent
  const markReminderSent = async (appointmentId) => {
    try {
      const res = await authFetch(`${API}/appointments/${appointmentId}/reminder-sent`, {
        method: "PATCH"
      });
      if (res.ok) {
        setAppointments(prev => prev.map(apt => 
          apt.appointment_id === appointmentId 
            ? { ...apt, reminder_sent: true, reminder_sent_at: new Date().toISOString() }
            : apt
        ));
        toast.success("Promemoria segnato come inviato");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  // Mark all reminders as sent
  const markAllRemindersSent = async () => {
    const ids = reminderPhones.map(p => p.appointment_id);
    try {
      const res = await authFetch(`${API}/appointments/bulk-reminder-sent`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ids)
      });
      if (res.ok) {
        setAppointments(prev => prev.map(apt => 
          ids.includes(apt.appointment_id)
            ? { ...apt, reminder_sent: true, reminder_sent_at: new Date().toISOString() }
            : apt
        ));
        setShowReminderDialog(false);
        toast.success(`${ids.length} promemoria segnati come inviati`);
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  // Open WhatsApp for single contact
  const openWhatsApp = (phone, message) => {
    const phoneWithCountry = phone.startsWith('39') ? phone : `39${phone}`;
    const url = `https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="appointments-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Appuntamenti</h1>
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open);
          if (!open) {
            setEditingAppointment(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="new-appointment-btn" className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Appuntamento
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">
                {editingAppointment ? "Modifica Appuntamento" : "Nuovo Appuntamento"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Customer Selection */}
              <div className="bg-zinc-800/50 p-4 rounded border border-zinc-700">
                <h3 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  DATI CLIENTE
                </h3>
                <div className="form-group mb-3">
                  <label className="form-label">Seleziona da Anagrafica</label>
                  <Select value={selectedCustomerId || "new"} onValueChange={(v) => handleCustomerSelect(v === "new" ? "" : v)}>
                    <SelectTrigger className="bg-zinc-900">
                      <SelectValue placeholder="- Seleziona cliente esistente -" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">-- Nuovo cliente --</SelectItem>
                      {customers.map(c => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>
                          {c.name} {c.phone ? `(${c.phone})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Nome Cliente *</label>
                    <input
                      data-testid="input-customer-name"
                      type="text"
                      className="form-input"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice Fiscale</label>
                    <input
                      type="text"
                      className="form-input uppercase"
                      maxLength={16}
                      value={formData.customer_fiscal_code}
                      onChange={(e) => setFormData({...formData, customer_fiscal_code: e.target.value.toUpperCase()})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      data-testid="input-customer-email"
                      type="email"
                      className="form-input"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Telefono *</label>
                    <input
                      data-testid="input-customer-phone"
                      type="tel"
                      className="form-input"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group col-span-2">
                    <label className="form-label">Indirizzo</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.customer_address}
                      onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Section with Lookup */}
              <div className="bg-zinc-800/50 p-4 rounded border border-zinc-700">
                <h3 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                  <Car className="w-4 h-4" />
                  DATI VEICOLO
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="form-group col-span-2">
                    <label className="form-label">Targa Veicolo *</label>
                    <div className="flex gap-2">
                      <input
                        data-testid="input-vehicle-plate"
                        type="text"
                        className="form-input uppercase flex-1"
                        value={formData.vehicle_plate}
                        onChange={(e) => setFormData({...formData, vehicle_plate: e.target.value.toUpperCase()})}
                        required
                        placeholder="AB123CD"
                      />
                      <Button
                        type="button"
                        onClick={handleVehicleLookup}
                        disabled={lookupLoading || !formData.vehicle_plate}
                        className="bg-primary hover:bg-primary/80"
                      >
                        {lookupLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Anno</label>
                    <input
                      type="text"
                      className="form-input"
                      maxLength={4}
                      value={formData.vehicle_year}
                      onChange={(e) => setFormData({...formData, vehicle_year: e.target.value.replace(/\D/g, '')})}
                      placeholder="2020"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Marca</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.vehicle_brand}
                      onChange={(e) => setFormData({...formData, vehicle_brand: e.target.value})}
                      placeholder="Es: FIAT"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Modello *</label>
                    <input
                      data-testid="input-vehicle-model"
                      type="text"
                      className="form-input"
                      value={formData.vehicle_model}
                      onChange={(e) => setFormData({...formData, vehicle_model: e.target.value})}
                      required
                      placeholder="Es: 500"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Versione</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.vehicle_version}
                      onChange={(e) => setFormData({...formData, vehicle_version: e.target.value})}
                      placeholder="Es: 1.2 Lounge"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Telaio (VIN)</label>
                    <input
                      type="text"
                      className="form-input uppercase"
                      maxLength={17}
                      value={formData.vehicle_vin}
                      onChange={(e) => setFormData({...formData, vehicle_vin: e.target.value.toUpperCase()})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Alimentazione</label>
                    <Select 
                      value={formData.vehicle_fuel_type} 
                      onValueChange={(v) => setFormData({...formData, vehicle_fuel_type: v})}
                    >
                      <SelectTrigger className="bg-zinc-900">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Benzina">Benzina</SelectItem>
                        <SelectItem value="Diesel">Diesel</SelectItem>
                        <SelectItem value="GPL">GPL</SelectItem>
                        <SelectItem value="Metano">Metano</SelectItem>
                        <SelectItem value="Elettrico">Elettrico</SelectItem>
                        <SelectItem value="Ibrido Benzina">Ibrido Benzina</SelectItem>
                        <SelectItem value="Ibrido Diesel">Ibrido Diesel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Appointment Details */}
              <div className="bg-zinc-800/50 p-4 rounded border border-zinc-700">
                <h3 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  DETTAGLI APPUNTAMENTO
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className="form-label">Tipo Servizio *</label>
                    <select
                      data-testid="select-service-type"
                      className="form-select"
                      value={formData.service_type}
                      onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                      required
                    >
                      <option value="">Seleziona...</option>
                      {serviceTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Data *</label>
                    <input
                      data-testid="input-appointment-date"
                      type="date"
                      className="form-input"
                      value={formData.appointment_date}
                      onChange={(e) => setFormData({...formData, appointment_date: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ora *</label>
                    <input
                      data-testid="input-appointment-time"
                      type="time"
                      className="form-input"
                      value={formData.appointment_time}
                      onChange={(e) => setFormData({...formData, appointment_time: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Note</label>
                  <textarea
                    data-testid="input-notes"
                    className="form-textarea h-24"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowDialog(false)}
                  className="text-zinc-400"
                >
                  Annulla
                </Button>
                <Button data-testid="submit-appointment-btn" type="submit" className="btn-primary">
                  {editingAppointment ? "Aggiorna" : "Crea Appuntamento"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reminder Section for Tomorrow */}
      {tomorrowAppointments.length > 0 && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-600/5 border border-green-500/30 rounded-sm p-5" data-testid="reminder-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-green-500/20 p-2 rounded-full">
                <Bell className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-bold uppercase font-['Barlow_Condensed']">
                  Promemoria Domani
                </h3>
                <p className="text-zinc-400 text-sm">
                  {tomorrowAppointments.length} appuntament{tomorrowAppointments.length === 1 ? 'o' : 'i'} per domani
                  {tomorrowAppointments.filter(a => a.reminder_sent).length > 0 && (
                    <span className="text-green-400 ml-2">
                      ({tomorrowAppointments.filter(a => a.reminder_sent).length} già notificat{tomorrowAppointments.filter(a => a.reminder_sent).length === 1 ? 'o' : 'i'})
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Button
              onClick={handleBulkReminder}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="bulk-reminder-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Copia e Invia Tutti
            </Button>
          </div>
          
          {/* Quick list of tomorrow's appointments */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {tomorrowAppointments.map((apt) => (
              <div 
                key={apt.appointment_id}
                className={`bg-zinc-900/50 border rounded-sm p-3 flex items-center justify-between ${
                  apt.reminder_sent ? 'border-green-500/30' : 'border-zinc-700'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-primary font-['JetBrains_Mono'] text-sm">{apt.appointment_time}</span>
                    {apt.reminder_sent && (
                      <CheckCircle2 className="w-4 h-4 text-green-400" title="Promemoria inviato" />
                    )}
                  </div>
                  <p className="text-white text-sm truncate">{apt.customer_name}</p>
                  <p className="text-zinc-500 text-xs truncate">{apt.vehicle_plate}</p>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      copyMessageToClipboard(generateReminderMessage(apt));
                    }}
                    className="text-zinc-400 hover:text-white h-8 w-8"
                    title="Copia messaggio"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openWhatsApp(apt.customer_phone, generateReminderMessage(apt))}
                    className="text-green-500 hover:text-green-400 h-8 w-8"
                    title="Apri WhatsApp"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  {!apt.reminder_sent && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => markReminderSent(apt.appointment_id)}
                      className="text-zinc-400 hover:text-green-400 h-8 w-8"
                      title="Segna come inviato"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl" aria-describedby="reminder-dialog-description">
          <DialogHeader>
            <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-500" />
              Invio Promemoria WhatsApp
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p id="reminder-dialog-description" className="text-zinc-400 text-sm">
              Per ogni cliente, copia il messaggio e invialo tramite WhatsApp. Poi clicca il segno di spunta per tracciare l'invio.
            </p>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {reminderPhones.map((item, idx) => (
                <div key={idx} className="bg-zinc-800/50 border border-zinc-700 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-white font-semibold">{item.name}</p>
                      <p className="text-primary font-['JetBrains_Mono'] text-sm">Ore {item.time}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={`https://wa.me/${item.phoneWithCountry}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-500 hover:text-green-400 flex items-center gap-1 text-sm"
                      >
                        <Phone className="w-4 h-4" />
                        +{item.phoneWithCountry}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyMessageToClipboard(item.message)}
                      className="flex-1"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copia Messaggio
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openWhatsApp(item.phone, item.message)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Apri WhatsApp
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowReminderDialog(false)}>
              Chiudi
            </Button>
            <Button onClick={markAllRemindersSent} className="bg-green-600 hover:bg-green-700">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Segna Tutti come Inviati
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => date && setSelectedDate(date)}
            modifiers={{ hasAppointment: datesWithAppointments }}
            modifiersStyles={{ hasAppointment: { fontWeight: "bold" } }}
            className="w-full"
          />
        </div>

        {/* Appointments List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed']">
              {selectedDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
            </h2>
            <span className="text-zinc-500">
              {getAppointmentsForDate(selectedDate).length} appuntamenti
            </span>
          </div>

          {getAppointmentsForDate(selectedDate).length > 0 ? (
            <div className="space-y-3">
              {getAppointmentsForDate(selectedDate).map((apt) => (
                <div 
                  key={apt.appointment_id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-4 card-hover"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-primary font-['JetBrains_Mono']">
                          {apt.appointment_time}
                        </span>
                        <span className={`px-2 py-0.5 rounded-sm text-xs uppercase ${statusColors[apt.status]}`}>
                          {statusLabels[apt.status]}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div className="flex items-center gap-2 text-zinc-400">
                          <User className="w-4 h-4" />
                          <span>{apt.customer_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Car className="w-4 h-4" />
                          <span>{apt.vehicle_model} - {apt.vehicle_plate}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Phone className="w-4 h-4" />
                          <span>{apt.customer_phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Mail className="w-4 h-4" />
                          <span className="truncate">{apt.customer_email}</span>
                        </div>
                      </div>
                      <div className="mt-3 text-sm">
                        <span className="text-zinc-500">Servizio:</span>
                        <span className="text-white ml-2">{apt.service_type}</span>
                      </div>
                      {apt.notes && (
                        <div className="mt-2 text-sm text-zinc-500">
                          {apt.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select
                        value={apt.status}
                        onValueChange={(value) => handleStatusChange(apt.appointment_id, value)}
                      >
                        <SelectTrigger className="w-32 bg-zinc-950 border-zinc-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="scheduled">Programmato</SelectItem>
                          <SelectItem value="in_progress">In Corso</SelectItem>
                          <SelectItem value="completed">Completato</SelectItem>
                          <SelectItem value="cancelled">Annullato</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`whatsapp-card-btn-${apt.appointment_id}`}
                        onClick={() => sendWhatsApp(apt)}
                        disabled={!apt.customer_phone}
                        className="text-green-500 hover:text-green-400 hover:bg-green-500/10"
                        title="Invia via WhatsApp"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => sendEmail(apt)}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        title="Invia via Email"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(apt)}
                        className="text-zinc-400 hover:text-white"
                        title="Modifica"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(apt.appointment_id)}
                        className="text-zinc-400 hover:text-red-400"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <CalendarIcon className="w-16 h-16 empty-state-icon" />
              <p className="empty-state-title">Nessun appuntamento</p>
              <p className="empty-state-text">Non ci sono appuntamenti per questa data</p>
            </div>
          )}

          {/* All Appointments */}
          <div className="mt-8">
            <h2 className="text-lg font-bold text-white uppercase font-['Barlow_Condensed'] mb-4">
              Tutti gli Appuntamenti
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Ora</th>
                    <th>Cliente</th>
                    <th>Telefono</th>
                    <th>Veicolo</th>
                    <th>Servizio</th>
                    <th>Stato</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((apt) => (
                    <tr key={apt.appointment_id}>
                      <td className="font-['JetBrains_Mono']">{apt.appointment_date}</td>
                      <td className="font-['JetBrains_Mono'] text-primary">{apt.appointment_time}</td>
                      <td>{apt.customer_name}</td>
                      <td className="font-['JetBrains_Mono'] text-sm">{apt.customer_phone}</td>
                      <td>{apt.vehicle_plate}</td>
                      <td>{apt.service_type}</td>
                      <td>
                        <span className={`px-2 py-0.5 rounded-sm text-xs uppercase ${statusColors[apt.status]}`}>
                          {statusLabels[apt.status]}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`whatsapp-btn-${apt.appointment_id}`}
                            onClick={() => sendWhatsApp(apt)}
                            disabled={!apt.customer_phone}
                            className="text-green-500 hover:text-green-400 hover:bg-green-500/10 h-8 w-8"
                            title="Invia via WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(apt)}
                            className="text-zinc-400 hover:text-white h-8 w-8"
                            title="Modifica"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
